import type { Components, Theme } from '@mui/material/styles'
import { getContrastColor } from 'utils/parsers'
import { fieldBorderRest } from 'components/UI/fieldMetrics'

export const MuiButton: Components<Theme>['MuiButton'] = {
  defaultProps: {
    variant: 'text',
    size: 'medium',
  },
  styleOverrides: {
    root: ({ theme }) => ({
      textTransform: 'initial',
      fontWeight: 500,
      color: theme.palette.text.primary,
      borderRadius: '8px',
      padding: 0,
      /**
       * A bordered button's outline — the SAME value a field uses, because it is
       * the same visual concept: a control's resting boundary on a near-white
       * ground. Kept as one token so the "Send login code" button and the Email
       * field stacked directly above it cannot drift apart.
       *
       * ## Why this is here and not on the variants
       *
       * MEASURED on /sign-in before this: "Send login code" (`MuiButton-outlined`)
       * and "Sign in with Google" (`@repo/ui`'s `secondary`, which lands on
       * `MuiButton-text`) BOTH rendered #E4E4E7 at **1.27:1** — the exact value
       * and the exact failure the fields had before 4b3bff7bc, in a component the
       * fix never reached.
       *
       * Neither was using the colour its source claims. `MuiButton`'s `outlined`
       * variant below authors `grey[300]` and `@repo/ui`'s `secondary` authors
       * `primary[100]`; neither renders, because `index.css`'s
       * `#vera-shadow-root *` sets `border-color` at ID specificity (0-1-0-0) and
       * beats every emotion class (0-0-1-0). That file's own comment names the
       * consequence: "the reason the app has ~20 border colours in source that
       * never render." So `--border` (#E4E4E7) was the de facto colour of every
       * border in the app, whatever the component asked for.
       *
       * `!important` is therefore load-bearing, not defensive — it is the only
       * form that survives, exactly as `fieldMetrics`' own borders discovered.
       *
       * ## Why `borderColor` alone, on `root`
       *
       * Setting only the COLOUR and never the width makes this a no-op wherever
       * there is no border to paint: the primary `contained` button computes
       * `border: 0px none` and is untouched. So one rule reaches both bordered
       * variants — which are two different MUI variants and could not have been
       * caught by one `props:` match — without inventing a list of which
       * variants happen to have an outline today.
       */
      borderColor: `${fieldBorderRest(theme)} !important`,
      /*
       * LIGHT ONLY. Dark mode's ground is near-black, where a faint border is not
       * the legibility problem it is on white — and dark is approved as-is, so it
       * keeps its own `--border`. Without this the `!important` above would win
       * there too and repaint every bordered button: measured at 101/255 on the
       * dark sign-in, which is a visible change to a surface nobody asked to move.
       */
      '.dark &': { borderColor: 'var(--border) !important' },
    }),
  },
  variants: [
    {
      props: { variant: 'contained' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.primary.main,
        color: getContrastColor(theme.palette.primary.main),
        ':hover': {
          opacity: 0.9,
        },
        // VERA-304: a disabled PRIMARY keeps its own colour and is only slightly
        // dimmed — it must NOT turn into a grey slab.
        //
        // This replaces the VERA-269 rule that repainted it `secondary.main` (a
        // near-white grey), which read as a different, inert control rather than "the
        // primary action, not yet available": the user loses the visual anchor for
        // where the CTA is while filling the form.
        //
        // Fixed HERE, in the theme, rather than at the call site, because this is the
        // recurring version of the bug: `@repo/ui`'s Button already had exactly this
        // treatment (VERA-291, primary stays black at opacity .85), but ~40 call sites
        // in this app use MUI's `variant='contained'` instead of the shared
        // `variant='primary'`, so none of them inherited it. Matching the rule in the
        // theme means every one of those is fixed at once and can't regress
        // individually. (§42: improve the shared layer, don't patch per surface.)
        '&.Mui-disabled': {
          background: theme.palette.primary.main,
          color: getContrastColor(theme.palette.primary.main),
          // 0.85 — the SAME value @repo/ui's Button uses for a disabled primary
          // (VERA-291), not MUI's default ~0.38. The point is a slight tint: the
          // button stays visibly the primary colour so it remains the anchor for
          // "this is the action", just clearly not-yet-available.
          opacity: 0.85,
        },
      }),
    },
    {
      props: { size: 'medium' },
      style: {
        fontSize: 14,
        lineHeight: '44px',
        height: '44px',
        fontWeight: 500,
      },
    },
    {
      props: { size: 'large' },
      style: {
        fontSize: 20,
        lineHeight: '77px',
        height: '77px',
        textTransform: 'uppercase',
      },
    },
    {
      props: { size: 'large', variant: 'contained' },
      style: {
        padding: '9px 18px',
      },
    },
    {
      props: { variant: 'outlined' },
      style: ({ theme }) => ({
        background: 'white',
        /* Width only — the COLOUR comes from `root`'s `borderColor`, which is the
           one form that survives index.css. This said `grey[300]` (#B0B0B0,
           2.17:1) for as long as it has existed and never once rendered it. */
        border: `1px solid ${fieldBorderRest(theme)}`,
        color: theme.palette.text.primary,
        ':hover': {
          border: `1px solid ${fieldBorderRest(theme)}`,
        },
        '&.Mui-disabled': {
          borderColor: theme.palette.grey[200],
          background: theme.palette.grey[50],
        },
      }),
    },
    {
      props: { variant: 'iconBtn' },
      style: ({ theme }) => ({
        width: '44px',
        height: '44px',
        minWidth: 'auto',
        padding: '0',
        margin: '0 4px',
        background: '#f5f5f5',
        color: 'black',
        ':hover': { background: '#e7e7e7' },
        '.MuiButton-endIcon': {
          margin: '0',
          color: 'black',
        },
      }),
    },
  ],
}
